import React, { useEffect, useState } from 'react';
import {
  Modal,
  Group,
  Button,
  Stack,
  Text,
  Table,
  Title,
  LoadingOverlay,
  Radio,
  Avatar,
  TextInput,
  Badge,
  Checkbox,
  Paper,
  Switch,
  Box,
  Select,
  Progress,
  Pagination
} from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { IconSearch, IconArrowLeft, IconUserCheck, IconCheck } from '@tabler/icons-react';
import { Participant } from 'pages/app/LIA/AssessmentSchedules/utils/schema';
import { dataBinusService } from 'services/dataBinusService';
import useDebounce from 'hooks/useDebounce';

/* eslint-disable no-unused-vars */
interface StandaloneRespondentModalProps {
  opened: boolean;
  onClose: () => void;
  existingRespondents: Participant[];
  onSubmit: (selectedRespondents: Participant[]) => void;
  participants?: Participant[];
}
/* eslint-enable no-unused-vars */

interface RespondentFilterOptions {
  isPeer: boolean;
  isSupervisor: boolean;
  isSubordinate: boolean;
}

type SelectionMode = 'auto' | 'manual';

const ALL_PARTICIPANTS_VALUE = 'all_participants';

const ParameterChangeIndicator: React.FC<{
  hasChanged: boolean;
  lastSearched: boolean;
}> = ({ hasChanged, lastSearched }) => {
  if (!lastSearched) return null;

  return (
    <Group gap='xs' mt='xs'>
      <Badge size='sm' color={hasChanged ? 'orange' : 'green'} variant='light'>
        {hasChanged ? 'Parameters Changed' : 'Up to Date'}
      </Badge>
      {hasChanged ? (
        <Text size='xs' c='dimmed'>
          Click "Generate Respondents" to search with new parameters
        </Text>
      ) : (
        <Text size='xs' c='dimmed'>
          There are no changes in the parameters
        </Text>
      )}
    </Group>
  );
};

const StandaloneRespondentModal: React.FC<StandaloneRespondentModalProps> = ({
  opened,
  onClose,
  existingRespondents,
  onSubmit,
  participants = []
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedParticipant, setSelectedParticipant] = useState<Participant | null>(null);
  const [selectedRespondents, setSelectedRespondents] = useState<Participant[]>([]);
  const [activeTab, setActiveTab] = useState<'participants' | 'respondents'>('participants');
  const [searchEmployee, setSearchEmployee] = useState(true);
  const [searchLecturer, setSearchLecturer] = useState(false);
  const [respondentFilters, setRespondentFilters] = useState<RespondentFilterOptions>({
    isPeer: true,
    isSupervisor: true,
    isSubordinate: true
  });

  const [hasSearched, setHasSearched] = useState(false);
  const [shouldFetchAuto, setShouldFetchAuto] = useState(false);
  const [lastAutoSearchParams, setLastAutoSearchParams] = useState<{
    participant: string;
    types: typeof autoSelectTypes;
    mingrade: number;
  } | null>(null);

  const [selectionMode, setSelectionMode] = useState<SelectionMode>('auto');
  const [autoSelectTypes, setAutoSelectTypes] = useState<{
    peer: boolean;
    supervisor: boolean;
    subordinate: boolean;
  }>({
    peer: true,
    supervisor: true,
    subordinate: false
  });
  const [retrievedRespondents, setRetrievedRespondents] = useState<Participant[]>([]);
  const [isLoadingAutoGeneration, setIsLoadingAutoGeneration] = useState(false);
  const [selectedParticipantForAuto, setSelectedParticipantForAuto] =
    useState<string>(ALL_PARTICIPANTS_VALUE);
  const [mingrade, setMingrade] = useState<number>(10);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [generationProgress, setGenerationProgress] = useState<{
    current: number;
    total: number;
    isProcessing: boolean;
  }>({
    current: 0,
    total: 0,
    isProcessing: false
  });

  const [participantSelectorModal, setParticipantSelectorModal] = useState<{
    opened: boolean;
    respondent: Participant | null;
  }>({ opened: false, respondent: null });

  const [selectedParticipantsForModal, setSelectedParticipantsForModal] = useState<string[]>([]);

  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const normalizeRespondentType = (rt?: string) => {
    if (!rt) return 'Peer';
    const t = rt.toString().trim();
    const up = t.toUpperCase();
    if (up === 'SPV' || up === 'SUPERVISOR') return 'Supervisor';
    if (up === 'PEER') return 'Peer';
    if (up === 'SUBORDINATE') return 'Subordinate';
    return 'Peer';
  };

  const deriveTypeFromRawOrAuto = (rawType: any) => {
    if (rawType) return normalizeRespondentType(rawType);
    if (autoSelectTypes.peer) return 'Peer';
    if (autoSelectTypes.supervisor) return 'Supervisor';
    return 'Subordinate';
  };

  useEffect(() => {
    if (opened) {
      setSelectedRespondents([]);
      setSearchQuery('');
      setActiveTab('participants');
      setSelectedParticipant(null);
      setSearchEmployee(true);
      setSearchLecturer(false);
      setSelectionMode('auto');
      setRetrievedRespondents([]);
      setSelectedParticipantForAuto(ALL_PARTICIPANTS_VALUE);
      setCurrentPage(1);
      setPageSize(10);
      setGenerationProgress({
        current: 0,
        total: 0,
        isProcessing: false
      });
      setHasSearched(false);
      setShouldFetchAuto(false);
      setLastAutoSearchParams(null);
    }
  }, [opened, participants]);

  const { data: emplecData, isLoading: isLoadingEmplec } = useQuery({
    queryKey: ['listEmplec', debouncedSearchQuery, searchEmployee, searchLecturer],
    queryFn: () =>
      dataBinusService.getEmplecList({
        search: debouncedSearchQuery,
        isEmployee: searchEmployee,
        isLecturer: searchLecturer
      }),
    enabled:
      opened &&
      selectionMode === 'manual' &&
      activeTab === 'participants' &&
      debouncedSearchQuery.length > 2
  });

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const { data: respondents, isLoading: isLoadingRespondents } = useQuery({
    queryKey: [
      'respondents',
      selectedParticipant?.email || selectedParticipant?.binusianId,
      respondentFilters
    ],
    queryFn: () =>
      dataBinusService.getRespondentList({
        email: selectedParticipant?.email || selectedParticipant?.binusianId || '',
        ispeer: respondentFilters.isPeer,
        isspv: respondentFilters.isSupervisor,
        issubordinate: respondentFilters.isSubordinate,
        mingrade: mingrade
      }),
    enabled:
      opened && selectionMode === 'manual' && activeTab === 'respondents' && !!selectedParticipant
  });

  const { data: autoRespondents, isLoading: isLoadingAutoRespondents } = useQuery({
    queryKey: ['autoRespondents', selectedParticipantForAuto, autoSelectTypes, mingrade],
    queryFn: () => {
      if (selectedParticipantForAuto !== ALL_PARTICIPANTS_VALUE) {
        const selectedParticipant = participants.find(
          (p) => p.binusianId === selectedParticipantForAuto
        );

        if (!selectedParticipant) {
          return Promise.resolve({ data: [] });
        }

        return dataBinusService.getRespondentList({
          email: selectedParticipant.email || selectedParticipant.binusianId || '',
          ispeer: autoSelectTypes.peer,
          isspv: autoSelectTypes.supervisor,
          issubordinate: autoSelectTypes.subordinate,
          mingrade: mingrade
        });
      }

      return Promise.resolve({ data: [] });
    },
    enabled:
      shouldFetchAuto &&
      opened &&
      selectionMode === 'auto' &&
      selectedParticipantForAuto !== ALL_PARTICIPANTS_VALUE &&
      (autoSelectTypes.peer || autoSelectTypes.supervisor || autoSelectTypes.subordinate)
  });

  const getRespondentAssignments = (respondent: Participant) => {
    const existing = existingRespondents.filter((r) => r.binusianId === respondent.binusianId);
    return existing;
  };

  const isRespondentAssignedToParticipant = (respondent: Participant, participantId: string) => {
    const existing = existingRespondents.find(
      (r) =>
        r.binusianId === respondent.binusianId &&
        r.participants?.some((p) => p.binusianId === participantId)
    );
    return !!existing;
  };

  const handleSelectParticipant = (participant: Participant) => {
    setSelectedParticipant(participant);
    setActiveTab('respondents');
  };

  const handleToggleRespondent = (respondent: Participant, isExternal: boolean = false) => {
    if (!selectedParticipant) return;

    const targetParticipantId = selectedParticipant.binusianId;

    setSelectedRespondents((prev) => {
      const existsInSelected = prev.some(
        (r) =>
          r.binusianId === respondent.binusianId &&
          r.participants?.some((p) => p.binusianId === targetParticipantId)
      );

      if (existsInSelected) {
        return prev.filter(
          (r) =>
            !(
              r.binusianId === respondent.binusianId &&
              r.participants?.some((p) => p.binusianId === targetParticipantId)
            )
        );
      } else {
        return [
          ...prev,
          {
            ...respondent,
            participantId: targetParticipantId,
            participants: [
              {
                binusianId: targetParticipantId,
                isExternalStructural: isExternal
              }
            ]
          }
        ];
      }
    });
  };

  const handleToggleAutoRetrievedRespondent = (respondent: Participant) => {
    if (selectedParticipantForAuto === ALL_PARTICIPANTS_VALUE) {
      setSelectedParticipantsForModal([]);
      setParticipantSelectorModal({ opened: true, respondent });
      return;
    }

    const targetParticipantId = respondent.participantId || '';

    setSelectedRespondents((prev) => {
      const existsInSelected = prev.some(
        (r) =>
          r.binusianId === respondent.binusianId &&
          r.participants?.some((p) => p.binusianId === targetParticipantId)
      );

      if (existsInSelected) {
        return prev.filter(
          (r) =>
            !(
              r.binusianId === respondent.binusianId &&
              r.participants?.some((p) => p.binusianId === targetParticipantId)
            )
        );
      } else {
        return [
          ...prev,
          {
            ...respondent,
            participants: [
              {
                binusianId: targetParticipantId,
                isExternalStructural: false
              }
            ]
          }
        ];
      }
    });
  };

  const handleBackToParticipants = () => {
    setActiveTab('participants');
    setSelectedParticipant(null);
    setSelectedRespondents([]);
  };

  const handleSubmit = () => {
    // Consolidate multiple assignments of the same respondent
    const consolidatedRespondents: Participant[] = [];
    const respondentMap = new Map<string, Participant>();

    selectedRespondents.forEach((respondent) => {
      const existingEntry = respondentMap.get(respondent.binusianId);

      if (existingEntry) {
        // Merge participants arrays
        const existingParticipants = existingEntry.participants || [];
        const newParticipants = respondent.participants || [];

        existingEntry.participants = [
          ...existingParticipants,
          ...newParticipants.filter(
            (np) => !existingParticipants.some((ep) => ep.binusianId === np.binusianId)
          )
        ];
      } else {
        respondentMap.set(respondent.binusianId, { ...respondent });
      }
    });

    // Convert map to array
    respondentMap.forEach((respondent) => {
      consolidatedRespondents.push(respondent);
    });

    onSubmit(consolidatedRespondents);
    onClose();
  };

  const handleToggleAutoSelectType = (type: 'peer' | 'supervisor' | 'subordinate') => {
    setAutoSelectTypes((prev) => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  const fetchRespondentsForParticipant = async (participant: Participant) => {
    try {
      if (!participant.email && !participant.binusianId) {
        return [];
      }

      const response = await dataBinusService.getRespondentList({
        email: participant.email || participant.binusianId || '',
        ispeer: autoSelectTypes.peer,
        isspv: autoSelectTypes.supervisor,
        issubordinate: autoSelectTypes.subordinate,
        mingrade: mingrade
      });

      if (response?.data) {
        return response.data.map((r: any): Participant => {
          const rawType = r.respondentType;
          const type = deriveTypeFromRawOrAuto(rawType);

          return {
            binusianId: r.binusianId || `resp_${Math.random().toString(36).substr(2, 9)}`,
            name: r.nama || r.name || 'Unknown User',
            email: r.email || '',
            division: r.division || 'Unknown Division',
            type: type,
            participantId: participant.binusianId
          };
        });
      }

      return [];
    } catch {
      return [];
    }
  };

  const hasAutoSearchParamsChanged = () => {
    if (!lastAutoSearchParams) return true;

    return (
      lastAutoSearchParams.participant !== selectedParticipantForAuto ||
      lastAutoSearchParams.types.peer !== autoSelectTypes.peer ||
      lastAutoSearchParams.types.supervisor !== autoSelectTypes.supervisor ||
      lastAutoSearchParams.types.subordinate !== autoSelectTypes.subordinate ||
      lastAutoSearchParams.mingrade !== mingrade
    );
  };

  const handleAutoGenerateRespondents = async () => {
    if (!autoSelectTypes.peer && !autoSelectTypes.supervisor && !autoSelectTypes.subordinate) {
      return;
    }

    setLastAutoSearchParams({
      participant: selectedParticipantForAuto,
      types: { ...autoSelectTypes },
      mingrade: mingrade
    });

    setIsLoadingAutoGeneration(true);
    setRetrievedRespondents([]);
    setCurrentPage(1);
    setHasSearched(true);

    try {
      if (selectedParticipantForAuto === ALL_PARTICIPANTS_VALUE) {
        if (participants.length === 0) {
          return;
        }

        setGenerationProgress({
          current: 0,
          total: participants.length,
          isProcessing: true
        });

        let allRespondents: Participant[] = [];

        for (let i = 0; i < participants.length; i++) {
          const participant = participants[i];

          setGenerationProgress((prev) => ({
            ...prev,
            current: i + 1
          }));

          const respondentsForParticipant = await fetchRespondentsForParticipant(participant);
          const mappedRespondents = respondentsForParticipant.map((r: any) => ({
            ...r,
            participants: [
              {
                binusianId: participant.binusianId,
                isExternalStructural: false
              }
            ]
          }));
          allRespondents = [...allRespondents, ...mappedRespondents];
        }

        setGenerationProgress({
          current: 0,
          total: 0,
          isProcessing: false
        });

        const uniqueRespondents = allRespondents.filter(
          (respondent, index, self) =>
            index ===
            self.findIndex(
              (r) =>
                r.binusianId === respondent.binusianId &&
                r.participantId === respondent.participantId
            )
        );

        // No longer filter out existing respondents - show all
        setRetrievedRespondents(uniqueRespondents);
      } else {
        setShouldFetchAuto(true);
        const participant = participants.find((p) => p.binusianId === selectedParticipantForAuto);

        if (!participant) {
          return;
        }

        if (autoRespondents?.data) {
          const mappedRespondents = autoRespondents.data.map((r: any): Participant => {
            const rawType = r.respondentType;
            const type = deriveTypeFromRawOrAuto(rawType);

            return {
              binusianId: r.binusianId || `resp_${Math.random().toString(36).substr(2, 9)}`,
              name: r.nama || r.name || 'Unknown User',
              email: r.email || '',
              division: r.division || 'Unknown Division',
              type: type,
              participantId: selectedParticipantForAuto,
              participants: [
                {
                  binusianId: selectedParticipantForAuto,
                  isExternalStructural: false
                }
              ]
            };
          });

          // No longer filter out existing respondents - show all
          setRetrievedRespondents(mappedRespondents);
        } else {
          setRetrievedRespondents([]);
        }
      }
    } catch {
      throw new Error('Error fetching auto-generated respondents');
    } finally {
      setIsLoadingAutoGeneration(false);
    }
  };

  const employeeParticipants: Participant[] = [];
  if (emplecData?.employees?.data) {
    emplecData.employees.data.forEach((emp: any) => {
      employeeParticipants.push({
        binusianId: emp.binusianId,
        name: emp.name,
        division: emp.bu || 'Employee',
        type: 'Employee',
        email: emp.email
      });
    });
  }

  // Parse lecturer data from emplec endpoint
  const lecturerParticipants: Participant[] = [];
  if (emplecData?.lecturers?.data) {
    emplecData.lecturers.data.forEach((lec: any) => {
      lecturerParticipants.push({
        binusianId: lec.binusianId,
        name: lec.name,
        division: lec.fakultas || 'Lecturer',
        type: 'Lecturer',
        email: lec.email
      });
    });
  }

  useEffect(() => {
    if (
      shouldFetchAuto &&
      autoRespondents?.data &&
      selectedParticipantForAuto !== ALL_PARTICIPANTS_VALUE
    ) {
      const mappedRespondents = autoRespondents.data.map((r: any): Participant => {
        const rawType = r.respondentType;
        const type = deriveTypeFromRawOrAuto(rawType);

        return {
          binusianId: r.binusianId || `resp_${Math.random().toString(36).substr(2, 9)}`,
          name: r.nama || r.name || 'Unknown User',
          email: r.email || '',
          division: r.division || 'Unknown Division',
          type: type,
          participantId: selectedParticipantForAuto,
          participants: [
            {
              binusianId: selectedParticipantForAuto,
              isExternalStructural: false
            }
          ]
        };
      });

      // No longer filter out existing respondents - show all
      setRetrievedRespondents(mappedRespondents);
      setShouldFetchAuto(false);
    }
  }, [
    shouldFetchAuto,
    autoRespondents,
    selectedParticipantForAuto,
    autoSelectTypes,
    existingRespondents
  ]);

  const handleClearResults = () => {
    setRetrievedRespondents([]);
    setSelectedRespondents([]);
    setHasSearched(false);
    setShouldFetchAuto(false);
    setLastAutoSearchParams(null);
    setCurrentPage(1);
  };

  // Function to render participant table
  const renderParticipantTable = (participants: Participant[], participantType: string) => {
    if (participants.length === 0) {
      return (
        <Text c='dimmed' ta='center' mt='md'>
          No {participantType} found matching your search
        </Text>
      );
    }

    return (
      <Table striped border={1} mt='md'>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Name</Table.Th>
            <Table.Th>Email</Table.Th>
            <Table.Th>Type</Table.Th>
            <Table.Th style={{ width: 120 }}>Actions</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {participants.map((participant) => (
            <Table.Tr key={participant.binusianId}>
              <Table.Td>
                <Group>
                  <Avatar color='#7B2183' radius='xl'>
                    {participant.name && participant.name.length > 0
                      ? participant.name.charAt(0)
                      : '?'}
                  </Avatar>
                  <div>
                    <Text size='sm' fw={500}>
                      {participant.name || 'Unknown Name'}
                    </Text>
                    <Text size='xs' c='dimmed'>
                      {participant.binusianId}
                    </Text>
                  </div>
                </Group>
              </Table.Td>
              <Table.Td>{participant.email || 'N/A'}</Table.Td>
              <Table.Td>{participant.type || 'Unknown'}</Table.Td>
              <Table.Td>
                <Button
                  size='xs'
                  leftSection={<IconUserCheck size={16} />}
                  onClick={() => handleSelectParticipant(participant)}
                  color='#7B2183'
                >
                  Choose
                </Button>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    );
  };

  const renderAutoGeneratedRespondents = () => {
    if (retrievedRespondents.length === 0) {
      return (
        <Text c='dimmed' ta='center' mt='md'>
          No respondents found for the selected types. Try selecting different types or using manual
          search.
        </Text>
      );
    }

    const totalPages = Math.ceil(retrievedRespondents.length / pageSize);
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize, retrievedRespondents.length);
    const paginatedRespondents = retrievedRespondents.slice(startIndex, endIndex);

    return (
      <>
        <Table striped border={1} mt='md'>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th>Email</Table.Th>
              <Table.Th>Type</Table.Th>
              <Table.Th>Participant</Table.Th>
              <Table.Th style={{ width: 80 }}>Select</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {paginatedRespondents.map((respondent) => {
              // Get all assignments for this respondent
              const assignments = getRespondentAssignments(respondent);
              const isAlreadyAssigned = assignments.length > 0;

              // Check if selected for current context
              const targetParticipantId = respondent.participantId || '';
              const isSelectedForThis = selectedRespondents.some(
                (r) =>
                  r.binusianId === respondent.binusianId &&
                  r.participants?.some((p) => p.binusianId === targetParticipantId)
              );

              // Check if already assigned to this specific participant
              const isAlreadyAssignedToThis = isRespondentAssignedToParticipant(
                respondent,
                targetParticipantId
              );

              // Check if this respondent has new assignments in selectedRespondents
              const newAssignmentsCount = selectedRespondents.filter(
                (r) => r.binusianId === respondent.binusianId
              ).length;
              const hasNewAssignments = newAssignmentsCount > 0;

              // Get participant name if participantId is available
              const participant = participants.find(
                (p) => p.binusianId === respondent.participantId
              );
              const participantName = participant ? participant.name : 'Unknown';

              return (
                <Table.Tr key={`${respondent.binusianId}-${respondent.participantId}`}>
                  <Table.Td>
                    <Group>
                      <Avatar color='#7B2183' radius='xl'>
                        {respondent.name && respondent.name.length > 0
                          ? respondent.name.charAt(0)
                          : '?'}
                      </Avatar>
                      <div>
                        <Text size='sm' fw={500}>
                          {respondent.name}
                        </Text>
                        <Text size='xs' c='dimmed'>
                          {respondent.binusianId}
                        </Text>
                        {isAlreadyAssigned && (
                          <Badge size='xs' color='blue' variant='light' mt={4}>
                            Assigned to {assignments.length} participant(s)
                          </Badge>
                        )}
                        {isAlreadyAssignedToThis && (
                          <Text size='xs' c='orange' mt={2}>
                            Already assigned to this participant
                          </Text>
                        )}
                        {hasNewAssignments && (
                          <Badge size='xs' color='green' variant='filled' mt={4}>
                            +{newAssignmentsCount} new assignment(s)
                          </Badge>
                        )}
                      </div>
                    </Group>
                  </Table.Td>
                  <Table.Td>{respondent.email}</Table.Td>
                  <Table.Td>{respondent.type}</Table.Td>
                  <Table.Td>
                    <Text size='sm'>{participantName}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Checkbox
                      checked={isSelectedForThis}
                      onChange={() => handleToggleAutoRetrievedRespondent(respondent)}
                    />
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>

        <Group justify='space-between' mt={'md'}>
          <Select
            value={pageSize.toString()}
            onChange={(value) => {
              const newSize = parseInt(value || '5', 10);
              setPageSize(newSize);
              setCurrentPage(1);
            }}
            data={[
              { value: '5', label: '5' },
              { value: '10', label: '10' },
              { value: '20', label: '20' },
              { value: '50', label: '50' }
            ]}
            style={{ width: 80 }}
          />

          <Pagination
            total={totalPages}
            value={currentPage}
            onChange={handlePageChange}
            siblings={1}
            boundaries={1}
          />
        </Group>
      </>
    );
  };

  // Create participant select options including "All Participants"
  const participantSelectOptions = [
    { value: ALL_PARTICIPANTS_VALUE, label: 'All Participants' },
    ...participants.map((p) => ({
      value: p.binusianId,
      label: `${p.name} (${p.binusianId})`
    }))
  ];

  return (
    <Modal opened={opened} centered onClose={onClose} size='xl' withCloseButton={false}>
      <Stack gap='md' p={16}>
        <Title order={3}>
          {selectionMode === 'auto'
            ? 'Auto-Generate Respondents'
            : activeTab === 'participants'
              ? 'Select Participant for Respondents'
              : `Select Respondents for ${selectedParticipant?.name || 'Participant'}`}
        </Title>

        {selectionMode === 'auto' ? (
          <Stack gap='md'>
            {participants.length > 0 ? (
              <>
                <Select
                  label='Select Participant'
                  placeholder='Choose a participant'
                  data={participantSelectOptions}
                  value={selectedParticipantForAuto}
                  onChange={(value) =>
                    setSelectedParticipantForAuto(value || ALL_PARTICIPANTS_VALUE)
                  }
                  searchable
                  required
                />

                <Text fw={500}>Select Respondent Types to Auto-Generate:</Text>

                <Paper withBorder p='md' radius='md'>
                  <Stack gap='md'>
                    <Group justify='space-between'>
                      <Text>Peer Respondents</Text>
                      <Switch
                        checked={autoSelectTypes.peer}
                        onChange={() => handleToggleAutoSelectType('peer')}
                        color='#7B2183'
                      />
                    </Group>

                    <Group justify='space-between'>
                      <Stack gap={'xs'}>
                        <Text>Select Upper Level</Text>
                        <Select
                          placeholder='Pick value'
                          value={
                            mingrade === 10
                              ? 'Minimum Supervisor'
                              : mingrade === 13
                                ? 'Minimum Associate Manager'
                                : mingrade === 15
                                  ? 'Minimum Manager'
                                  : mingrade === 19
                                    ? 'Minimum Senior Manager'
                                    : mingrade === 21
                                      ? 'Minimum General Manager'
                                      : mingrade === 23
                                        ? 'Minimum Associate Director'
                                        : mingrade === 26
                                          ? 'Minimum Director'
                                          : 'Minimum Supervisor'
                          }
                          onChange={(value: any) => {
                            if (value === 'Minimum Supervisor') {
                              setMingrade(10);
                            } else if (value === 'Minimum Associate Manager') {
                              setMingrade(13);
                            } else if (value === 'Minimum Manager') {
                              setMingrade(15);
                            } else if (value === 'Minimum Senior Manager') {
                              setMingrade(19);
                            } else if (value === 'Minimum General Manager') {
                              setMingrade(21);
                            } else if (value === 'Minimum Associate Director') {
                              setMingrade(23);
                            } else if (value === 'Minimum Director') {
                              setMingrade(26);
                            }
                          }}
                          data={[
                            'Minimum Supervisor',
                            'Minimum Associate Manager',
                            'Minimum Manager',
                            'Minimum Senior Manager',
                            'Minimum General Manager',
                            'Minimum Associate Director',
                            'Minimum Director'
                          ]}
                        />
                      </Stack>

                      <Switch
                        checked={autoSelectTypes.supervisor}
                        onChange={() => handleToggleAutoSelectType('supervisor')}
                        color='#7B2183'
                      />
                    </Group>

                    <Group justify='space-between'>
                      <Text>Subordinate Respondents</Text>
                      <Switch
                        checked={autoSelectTypes.subordinate}
                        onChange={() => handleToggleAutoSelectType('subordinate')}
                        color='#7B2183'
                      />
                    </Group>

                    <ParameterChangeIndicator
                      hasChanged={hasAutoSearchParamsChanged()}
                      lastSearched={hasSearched}
                    />

                    <Button
                      onClick={handleAutoGenerateRespondents}
                      loading={isLoadingAutoGeneration || isLoadingAutoRespondents}
                      disabled={
                        !autoSelectTypes.peer &&
                        !autoSelectTypes.supervisor &&
                        !autoSelectTypes.subordinate
                      }
                      color='#7B2183'
                      variant={hasAutoSearchParamsChanged() ? 'filled' : 'outline'}
                      leftSection={
                        hasAutoSearchParamsChanged() ? (
                          <IconSearch size={16} />
                        ) : (
                          <IconCheck size={16} />
                        )
                      }
                      fullWidth
                    >
                      {hasAutoSearchParamsChanged()
                        ? 'Generate Respondents'
                        : hasSearched
                          ? 'Regenerate Respondents'
                          : 'Generate Respondents'}
                    </Button>
                  </Stack>
                </Paper>

                {generationProgress.isProcessing && (
                  <Paper withBorder p='md' radius='md'>
                    <Text size='sm' mb='xs'>
                      Generating respondents: {generationProgress.current} of{' '}
                      {generationProgress.total} participants
                    </Text>
                    <Progress
                      value={(generationProgress.current / generationProgress.total) * 100}
                      color='#7B2183'
                      striped
                      animated
                      size='lg'
                    />
                  </Paper>
                )}

                <Box
                  style={{
                    position: 'relative',
                    minHeight: retrievedRespondents.length ? '300px' : 'auto'
                  }}
                >
                  <LoadingOverlay visible={isLoadingAutoGeneration || isLoadingAutoRespondents} />
                  {retrievedRespondents.length > 0 && (
                    <>
                      <Group justify='space-between'>
                        <Text fw={500} mt='md'>
                          Retrieved Respondents ({retrievedRespondents.length}):
                        </Text>
                        <Group>
                          <Button
                            size='xs'
                            variant='outline'
                            color='#7B2183'
                            onClick={() => {
                              const newSelections = retrievedRespondents.filter((r) => {
                                const targetParticipantId = r.participantId || '';
                                return !selectedRespondents.some(
                                  (sr) =>
                                    sr.binusianId === r.binusianId &&
                                    sr.participants?.some(
                                      (p) => p.binusianId === targetParticipantId
                                    )
                                );
                              });
                              setSelectedRespondents([...selectedRespondents, ...newSelections]);
                            }}
                          >
                            Select All
                          </Button>
                          <Button
                            size='xs'
                            variant='outline'
                            color='gray'
                            onClick={() => {
                              setSelectedRespondents((prev) =>
                                prev.filter(
                                  (r) =>
                                    !retrievedRespondents.some(
                                      (rr) => rr.binusianId === r.binusianId
                                    )
                                )
                              );
                            }}
                          >
                            Deselect All
                          </Button>
                          <Button
                            size='xs'
                            variant='outline'
                            color='red'
                            onClick={handleClearResults}
                          >
                            Clear Results
                          </Button>
                        </Group>
                      </Group>
                      {renderAutoGeneratedRespondents()}
                    </>
                  )}
                </Box>
              </>
            ) : (
              <Paper withBorder p='md' radius='md'>
                <Text c='dimmed' ta='center'>
                  No participants available. Please add participants first before generating
                  respondents.
                </Text>
              </Paper>
            )}
          </Stack>
        ) : (
          <>
            {activeTab === 'participants' && (
              <>
                <Group mb='md'>
                  <Radio
                    label='Search Employees'
                    checked={searchEmployee && !searchLecturer}
                    onChange={() => {
                      setSearchEmployee(true);
                      setSearchLecturer(false);
                    }}
                  />
                  <Radio
                    label='Search Lecturers'
                    checked={!searchEmployee && searchLecturer}
                    onChange={() => {
                      setSearchEmployee(false);
                      setSearchLecturer(true);
                    }}
                  />
                  <Radio
                    label='Search Both'
                    checked={searchEmployee && searchLecturer}
                    onChange={() => {
                      setSearchEmployee(true);
                      setSearchLecturer(true);
                    }}
                  />
                </Group>

                <TextInput
                  label='Search Participant'
                  placeholder='Search by name or ID...'
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.currentTarget.value)}
                  rightSection={<IconSearch size={16} />}
                />

                <div style={{ position: 'relative', minHeight: '300px' }}>
                  <LoadingOverlay visible={isLoadingEmplec && debouncedSearchQuery.length > 2} />

                  {debouncedSearchQuery.length > 2 && !isLoadingEmplec ? (
                    <Stack mt='md'>
                      {searchEmployee && (
                        <>
                          <Badge color='blue' size='lg' variant='light'>
                            Employees
                          </Badge>
                          {renderParticipantTable(employeeParticipants, 'employees')}
                        </>
                      )}

                      {searchLecturer && (
                        <>
                          <Badge color='green' size='lg' variant='light' mt='md'>
                            Lecturers
                          </Badge>
                          {renderParticipantTable(lecturerParticipants, 'lecturers')}
                        </>
                      )}

                      {employeeParticipants.length === 0 && lecturerParticipants.length === 0 && (
                        <Text c='dimmed' ta='center' mt='md'>
                          No participants found matching your search
                        </Text>
                      )}
                    </Stack>
                  ) : (
                    <Text c='dimmed' ta='center' mt='md'>
                      {debouncedSearchQuery.length > 0
                        ? isLoadingEmplec
                          ? 'Searching...'
                          : 'No participants found matching your search'
                        : 'Enter at least 3 characters to search'}
                    </Text>
                  )}
                </div>
              </>
            )}

            {activeTab === 'respondents' && selectionMode === 'manual' && (
              <div style={{ position: 'relative' }}>
                <Button
                  variant='outline'
                  mb='md'
                  leftSection={<IconArrowLeft size={16} />}
                  onClick={handleBackToParticipants}
                >
                  Back to Participants
                </Button>

                <Group mb='md'>
                  <Text fw={500}>Filter Respondents:</Text>
                  <Radio.Group defaultValue='all'>
                    <Group>
                      <Radio
                        value='peer'
                        label='Peer'
                        checked={
                          respondentFilters.isPeer &&
                          !respondentFilters.isSupervisor &&
                          !respondentFilters.isSubordinate
                        }
                        onChange={() =>
                          setRespondentFilters({
                            isPeer: true,
                            isSupervisor: false,
                            isSubordinate: false
                          })
                        }
                      />
                      <Radio
                        value='supervisor'
                        label='Supervisor'
                        checked={
                          !respondentFilters.isPeer &&
                          respondentFilters.isSupervisor &&
                          !respondentFilters.isSubordinate
                        }
                        onChange={() =>
                          setRespondentFilters({
                            isPeer: false,
                            isSupervisor: true,
                            isSubordinate: false
                          })
                        }
                      />
                      <Radio
                        value='subordinate'
                        label='Subordinate'
                        checked={
                          !respondentFilters.isPeer &&
                          !respondentFilters.isSupervisor &&
                          respondentFilters.isSubordinate
                        }
                        onChange={() =>
                          setRespondentFilters({
                            isPeer: false,
                            isSupervisor: false,
                            isSubordinate: true
                          })
                        }
                      />
                      <Radio
                        value='all'
                        label='All Types'
                        checked={
                          respondentFilters.isPeer &&
                          respondentFilters.isSupervisor &&
                          respondentFilters.isSubordinate
                        }
                        onChange={() =>
                          setRespondentFilters({
                            isPeer: true,
                            isSupervisor: true,
                            isSubordinate: true
                          })
                        }
                      />
                    </Group>
                  </Radio.Group>
                </Group>

                <div style={{ position: 'relative', minHeight: '300px' }}>
                  <LoadingOverlay visible={isLoadingRespondents} />

                  {respondents?.data && respondents.data.length > 0 ? (
                    <Table striped border={1}>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Name</Table.Th>
                          <Table.Th>Email</Table.Th>
                          <Table.Th>Role</Table.Th>
                          <Table.Th style={{ width: 80 }}>Select</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {respondents.data.map((respondent: any) => {
                          const mappedRespondent: Participant = {
                            binusianId: respondent.binusianId,
                            name: respondent.nama || 'Unknown',
                            division: respondent.division || 'Unknown',
                            type: respondent.respondentType
                              ? normalizeRespondentType(respondent.respondentType)
                              : 'Unknown',
                            email: respondent.email
                          };

                          const assignments = getRespondentAssignments(mappedRespondent);
                          const isAlreadyAssigned = assignments.length > 0;

                          const isAlreadyAssignedToThis = selectedParticipant
                            ? isRespondentAssignedToParticipant(
                                mappedRespondent,
                                selectedParticipant.binusianId
                              )
                            : false;

                          return (
                            <Table.Tr key={respondent.binusianId}>
                              <Table.Td>
                                <Group>
                                  <Avatar color='#7B2183' radius='xl'>
                                    {mappedRespondent.name && mappedRespondent.name.length > 0
                                      ? mappedRespondent.name.charAt(0)
                                      : '?'}
                                  </Avatar>
                                  <div>
                                    <Text size='sm' fw={500}>
                                      {mappedRespondent.name}
                                    </Text>
                                    <Text size='xs' c='dimmed'>
                                      {mappedRespondent.binusianId}
                                    </Text>
                                    {isAlreadyAssigned && (
                                      <Badge size='xs' color='blue' variant='light' mt={4}>
                                        Assigned to {assignments.length} participant(s)
                                      </Badge>
                                    )}
                                    {isAlreadyAssignedToThis && (
                                      <Text size='xs' c='orange' mt={2}>
                                        Already assigned to this participant
                                      </Text>
                                    )}
                                  </div>
                                </Group>
                              </Table.Td>
                              <Table.Td>{mappedRespondent.email}</Table.Td>
                              <Table.Td>{mappedRespondent.type}</Table.Td>
                              <Table.Td>
                                <Checkbox
                                  checked={selectedRespondents.some(
                                    (r) =>
                                      r.binusianId === mappedRespondent.binusianId &&
                                      r.participants?.some(
                                        (p) => p.binusianId === selectedParticipant?.binusianId
                                      )
                                  )}
                                  onChange={() => handleToggleRespondent(mappedRespondent)}
                                />
                              </Table.Td>
                            </Table.Tr>
                          );
                        })}
                      </Table.Tbody>
                    </Table>
                  ) : (
                    !isLoadingRespondents && (
                      <Text c='dimmed' ta='center' mt='md'>
                        No respondents found for this participant
                      </Text>
                    )
                  )}
                </div>
              </div>
            )}
          </>
        )}

        <Group justify='flex-end' mt='md'>
          <Button variant='outline' onClick={onClose} color='#7B2183'>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            color='#7B2183'
            disabled={selectedRespondents.length === 0}
          >
            {`Add ${selectedRespondents.length} Respondent${selectedRespondents.length !== 1 ? 's' : ''}`}
          </Button>
        </Group>
      </Stack>

      {/* Participant Selector Modal for Reassignment */}
      <Modal
        opened={participantSelectorModal.opened}
        onClose={() => {
          setParticipantSelectorModal({ opened: false, respondent: null });
          setSelectedParticipantsForModal([]);
        }}
        title='Select Participants to Assign'
        centered
        size='md'
      >
        <Stack gap='md'>
          <Text size='sm' c='dimmed'>
            {participantSelectorModal.respondent?.name} is already assigned. Select which
            participant(s) to assign this respondent to:
          </Text>

          {participants.length > 0 ? (
            <>
              <Group justify='space-between' mb='xs'>
                <Text size='sm' fw={500}>
                  {selectedParticipantsForModal.length} selected
                </Text>
                <Group gap='xs'>
                  <Button
                    size='xs'
                    variant='subtle'
                    color='#7B2183'
                    onClick={() => {
                      const availableParticipants = participants
                        .filter((p) => {
                          if (!participantSelectorModal.respondent) return false;
                          return !isRespondentAssignedToParticipant(
                            participantSelectorModal.respondent,
                            p.binusianId
                          );
                        })
                        .map((p) => p.binusianId);
                      setSelectedParticipantsForModal(availableParticipants);
                    }}
                  >
                    Select All Available
                  </Button>
                  <Button
                    size='xs'
                    variant='subtle'
                    color='gray'
                    onClick={() => setSelectedParticipantsForModal([])}
                  >
                    Clear
                  </Button>
                </Group>
              </Group>
              <Stack gap='xs' style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {participants.map((participant) => {
                  const isAlreadyAssigned = participantSelectorModal.respondent
                    ? isRespondentAssignedToParticipant(
                        participantSelectorModal.respondent,
                        participant.binusianId
                      )
                    : false;

                  const isSelected = selectedParticipantsForModal.includes(participant.binusianId);

                  return (
                    <Paper
                      key={participant.binusianId}
                      p='sm'
                      withBorder
                      style={{
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        backgroundColor: isSelected ? '#f8f0f9' : 'transparent'
                      }}
                      onClick={() => {
                        if (isSelected) {
                          setSelectedParticipantsForModal((prev) =>
                            prev.filter((id) => id !== participant.binusianId)
                          );
                        } else {
                          setSelectedParticipantsForModal((prev) => [
                            ...prev,
                            participant.binusianId
                          ]);
                        }
                      }}
                    >
                      <Group justify='space-between'>
                        <Group>
                          <Checkbox
                            checked={isSelected}
                            onChange={() => {
                              if (isSelected) {
                                setSelectedParticipantsForModal((prev) =>
                                  prev.filter((id) => id !== participant.binusianId)
                                );
                              } else {
                                setSelectedParticipantsForModal((prev) => [
                                  ...prev,
                                  participant.binusianId
                                ]);
                              }
                            }}
                            color='#7B2183'
                          />
                          <Avatar color='#7B2183' radius='xl'>
                            {participant.name?.[0] || '?'}
                          </Avatar>
                          <div>
                            <Text size='sm' fw={500}>
                              {participant.name}
                            </Text>
                            <Text size='xs' c='dimmed'>
                              {participant.binusianId}
                            </Text>
                          </div>
                        </Group>
                        {isAlreadyAssigned && (
                          <Badge size='sm' color='orange' variant='light'>
                            Already Assigned
                          </Badge>
                        )}
                      </Group>
                    </Paper>
                  );
                })}
              </Stack>
            </>
          ) : (
            <Text c='dimmed' ta='center'>
              No participants available
            </Text>
          )}

          <Group justify='flex-end' mt='md'>
            <Button
              variant='outline'
              onClick={() => {
                setParticipantSelectorModal({ opened: false, respondent: null });
                setSelectedParticipantsForModal([]);
              }}
              color='#7B2183'
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (
                  participantSelectorModal.respondent &&
                  selectedParticipantsForModal.length > 0
                ) {
                  const respondent = participantSelectorModal.respondent;
                  const newAssignments = selectedParticipantsForModal.map((participantId) => ({
                    ...respondent,
                    participantId: participantId,
                    participants: [
                      {
                        binusianId: participantId,
                        isExternalStructural: false
                      }
                    ]
                  }));
                  setSelectedRespondents((prev) => [...prev, ...newAssignments]);
                }

                setParticipantSelectorModal({ opened: false, respondent: null });
                setSelectedParticipantsForModal([]);
              }}
              color='#7B2183'
              disabled={selectedParticipantsForModal.length === 0}
            >
              Assign to {selectedParticipantsForModal.length} Participant(s)
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Modal>
  );
};

export default StandaloneRespondentModal;
